import { RichText } from '@/components/shared/rich-text'
import { SectionHeader } from '@/components/shared/section-header'
import { BlokProps } from '@/lib/types'
import { BentoGridCardStoryblok, BentoGridStoryblok } from '@/lib/types/component-types-sb'
import { cn } from '@/lib/utils'
import { storyblokEditable } from '@storyblok/react/rsc'
import { DynamicIcon } from 'lucide-react/dynamic'
import { ComponentProps, useMemo } from 'react'
import { InteractiveVisual } from '../interactive/registry'
import { Container } from '../shared/container'
import { StoryblokImage } from '../shared/storyblok-image'

function BentoGridCard({ blok }: BlokProps<BentoGridCardStoryblok>) {
  const sizeClass = () => {
    switch (blok.size) {
      case '1':
        return 'lg:col-span-1'
      case '2':
        return 'lg:col-span-2'
      case '3':
        return 'lg:col-span-3'
      case '4':
        return 'lg:col-span-4'
      case '5':
        return 'lg:col-span-5'
      case '7':
        return 'lg:col-span-7'
      case '8':
        return 'lg:col-span-8'
      case '9':
        return 'lg:col-span-9'
      case '10':
        return 'lg:col-span-10'
      case '11':
        return 'lg:col-span-11'
      case '12':
        return 'lg:col-span-12'
      case '6':
      default:
        return 'lg:col-span-6'
    }
  }

  const visualMode = useMemo(() => {
    if (blok.visual) {
      return 'interactive'
    }

    if (blok.visualAsset?.filename) {
      return 'asset'
    }

    return 'icon'
  }, [blok])

  return (
    <div
      {...storyblokEditable(blok)}
      className={cn('bg-background col-span-12 overflow-hidden', sizeClass())}
    >
      {visualMode === 'asset' && blok.visualAsset && (
        <StoryblokImage
          image={blok.visualAsset}
          className="h-auto w-full px-6 pt-6"
        />
      )}
      {visualMode === 'icon' && blok.icon && (
        <div className="h-auto w-full px-9 pt-6">
          <DynamicIcon
            name={blok.icon as ComponentProps<typeof DynamicIcon>['name']}
            strokeWidth={1.5}
            className="text-brand h-14 w-14"
          />
        </div>
      )}
      {visualMode === 'interactive' && blok.visual && (
        <div
          className={cn({
            'h-[350px] w-full overflow-hidden': true,
            'ps-9': Number(blok.size) > 4,
          })}
        >
          <InteractiveVisual
            className={'rounded-tr-none border-e-0'}
            componentId={blok.visual}
          />
        </div>
      )}
      <div className={cn('px-9 pb-11', visualMode === 'icon' ? 'pt-6' : 'pt-11')}>
        <h3 className="text-accent-foreground mb-2 text-xl font-semibold lg:text-xl">
          {blok.title}
        </h3>
        <RichText content={blok.content} />
      </div>
    </div>
  )
}

export function BentoGrid({ blok }: BlokProps<BentoGridStoryblok>) {
  return (
    <section
      {...storyblokEditable(blok)}
      className="border-border border-y"
    >
      <div className="border-border border-b">
        <Container className="py-16">
          <SectionHeader
            title={blok.title}
            icon={blok.descriptorIcon}
            preTitle={blok.descriptorTitle}
            content={blok.content}
          />
        </Container>
      </div>
      <Container>
        <div className="bg-border border-border grid grid-cols-12 gap-[1px] border-x">
          {blok.cards?.map((card) => (
            <BentoGridCard
              key={card._uid}
              blok={card}
            />
          ))}
        </div>
      </Container>
    </section>
  )
}
